import { createBrowserHistory } from 'history'

/**
 * Shared browser history used for imperative navigation and Redux location sync.
 * Replaces react-router v3 browserHistory during the v6 migration.
 */
export const appHistory = createBrowserHistory()

const REPLACE_WINDOW_MS = 10_000
const REPLACE_MAX_PER_WINDOW = 50

let replaceTimestamps = []
const recentRedirectAt = new Map()

export function normalizePathname(path) {
  if (path == null) return '/'
  const raw = typeof path === 'string' ? path : path.pathname || ''
  const pathname = raw.split('?')[0].split('#')[0] || '/'
  if (pathname.length > 1 && pathname.endsWith('/')) {
    return pathname.slice(0, -1)
  }
  return pathname || '/'
}

export function getLastPathSegment(pathname) {
  const pathMatch = normalizePathname(pathname).match(/([^/]+$)/)
  return pathMatch ? pathMatch[0] : ''
}

const INVALID_ROUTE_SEGMENT = /\/undefined(?:\/|$)/

/**
 * Returns false when a navigation target includes an unresolved "undefined" segment,
 * which otherwise resolves to a relative script URL on nested study routes and triggers
 * SyntaxError: Unexpected token '<' when the SPA index.html is returned.
 */
export function isValidNavigationTarget(path) {
  if (path == null) return false
  const pathname = normalizePathname(path)
  return !INVALID_ROUTE_SEGMENT.test(pathname)
}

/**
 * Build /studies/:studyID/:studyVersionID/:subroute with safe fallbacks.
 */
export function buildStudyRoutePath(studyID, studyVersionID, subroute = 'compliance') {
  const safeStudyID = studyID ?? ''
  const safeVersionID = studyVersionID ?? ''
  const safeSubroute = subroute || 'compliance'
  return `/studies/${safeStudyID}/${safeVersionID}/${safeSubroute}`
}

function guardNavigationTarget(path) {
  if (!isValidNavigationTarget(path)) {
    if (typeof __DEV__ !== 'undefined' && __DEV__) {
      console.warn('[navigation] Blocked navigation to invalid path:', path)
    }
    return false
  }
  return true
}

/**
 * Guards against auth/router redirect loops that exceed browser replaceState limits.
 * Returns true when a replace was performed.
 */
export function safeHistoryReplace(path, state) {
  const targetPathname = normalizePathname(path)
  const currentPathname = normalizePathname(appHistory.location.pathname)

  if (targetPathname === currentPathname) {
    return false
  }

  const now = Date.now()
  replaceTimestamps = replaceTimestamps.filter(timestamp => now - timestamp < REPLACE_WINDOW_MS)
  if (replaceTimestamps.length >= REPLACE_MAX_PER_WINDOW) {
    return false
  }

  const redirectKey = `${currentPathname}->${targetPathname}`
  const lastRedirectAt = recentRedirectAt.get(redirectKey)
  if (lastRedirectAt != null && now - lastRedirectAt < 1000) {
    return false
  }

  recentRedirectAt.set(redirectKey, now)
  replaceTimestamps.push(now)
  appHistory.replace(path, getHistoryState(path, state))
  return true
}

/**
 * history v5's push/replace default a missing second argument to null and overwrite
 * location.state. Callers that pass a v3-style `{ pathname, state }` object would
 * otherwise lose that state (e.g. All Schedules → Edit Schedule).
 */
export function getHistoryState(path, state) {
  if (state !== undefined) return state
  if (path != null && typeof path === 'object') return path.state
  return undefined
}

/**
 * v3-compatible browserHistory shim so existing imports keep working via webpack alias.
 */
export const browserHistory = {
  push: (path, state) => {
    if (!guardNavigationTarget(path)) return
    appHistory.push(path, getHistoryState(path, state))
  },
  replace: (path, state) => {
    if (!guardNavigationTarget(path)) return false
    return safeHistoryReplace(path, state)
  },
  go: n => appHistory.go(n),
  goBack: () => appHistory.back(),
  goForward: () => appHistory.forward(),
  listen: listener => appHistory.listen(listener),
  getCurrentLocation: () => appHistory.location,
  location: appHistory.location,
}

let navigateRef = null

export function setNavigate(navigate) {
  navigateRef = navigate
}

export function navigate(to, { replace = false, state } = {}) {
  if (!navigateRef) {
    if (replace) {
      browserHistory.replace(to, state)
    } else {
      browserHistory.push(to, state)
    }
    return
  }
  navigateRef(to, { replace, state })
}

export const push = to => navigate(to)
export const replace = to => navigate(to, { replace: true })

export default browserHistory
